/**
 * useDownloadAnswerPack (P155) -- fetch a signed assurance answer pack
 * (zip) as a Blob + trigger a browser download. Same posture as
 * useDownloadBoardPdf (bypasses api.request's JSON parse; manual bearer).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export class AnswerPackDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "AnswerPackDownloadError";
  }
}

function _filename(header: string | null, questionId: string): string {
  const fallback = `marsad-assurance-${questionId}.zip`;
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

async function _download(questionId: string): Promise<{ filename: string }> {
  const token = currentAuthToken();
  const response = await fetch(
    `/v1/assurance/answer/${encodeURIComponent(questionId)}/pack.zip`,
    {
      method: "GET",
      headers: {
        Accept: "application/zip",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail ?? detail;
    } catch {
      // non-JSON body; keep statusText
    }
    throw new AnswerPackDownloadError(response.status, detail);
  }
  const filename = _filename(response.headers.get("Content-Disposition"), questionId);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return { filename };
}

export function useDownloadAnswerPack(): UseMutationResult<
  { filename: string },
  AnswerPackDownloadError,
  string
> {
  return useMutation({ mutationFn: _download });
}
